import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import useFormStore from '../state/useFormStore';
import { config } from '../config';

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token de autenticación a las peticiones
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useFormStore.getState().token;
    
    // Si hay un token, agregarlo al header de Authorization
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Manejar errores específicos
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      console.error('Error de respuesta:', error.response.data);
      
      // Si el error es de autenticación (401)
      if (error.response.status === 401) {
        // Limpiar datos de autenticación
        localStorage.removeItem('auth_token');
        useFormStore.getState().setToken('');

        // Limpiar datos de la solicitud
        const hash = useFormStore.getState().hash;
        if (hash) {
          localStorage.removeItem(`prestix_${hash}`);
        }

        // Redirigir a la página de sesión expirada
        window.location.href = '/expirado';
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      console.error('Error de red:', error.request);
    } else {
      // Algo ocurrió al configurar la petición
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Funciones para hacer requests a la API
export const apiService = {
  // Autenticación (sin token)
  auth: {
    createSession: () => api.post('/auth/session', {
      application_token: config.business.applicationToken,
      domain: config.business.domain
    }),
  },
  
  // Clientes
  customers: {
    search: (document: string, gender?: string) => {
      let url = `/customers/search?document=${document}`;
      if (gender) {
        url += `&gender=${gender}`;
      }
      return api.get(url);
    },
  },
  
  // Solicitudes
  requests: {

    create: (data: {
      personalInfo: {
        firstname: string;
        lastname: string;
        gender: string;
        birthdate: string;
        cuil: string;
      };
      employmentInfo: {
        employmentStatus: string;
        bank: string;
      };
      addressContact: {
        mobile: string;
        email: string;
      };
    }) => api.post('/requests', data),

    getOffers: (requestUid: string) => api.get(`/requests/${requestUid}/offers`),

    selectPreOffer: (requestUid: string, data: {
        offerId: string;
        selectedAmount: number;
        selectedInstallmentAmount: number;
        selectedRate: number;
        selectedInstallments: number;
      }) => api.post(`/requests/${requestUid}/selected-preoffer`, data),

    selectOffer: (requestUid: string, data: {
        offerId: string;
        selectedAmount: number;
        selectedInstallmentAmount: number;
      }) => api.post(`/requests/${requestUid}/selected-offer`, data),  
  
    updateEmployment: (requestUid: string, data: {
      monthlyIncome: number;
      employmentStatus?: string;
      bank?: string;
      companyName?: string;
      companyIdentification?: string;
      joinDate?: string;
    }) => api.post(`/requests/${requestUid}/employment`, data),

    getContract: (requestUid: string) => api.get(`/requests/${requestUid}/contracts/latest`),

    signContract: (requestUid: string, data: {
      screenResolution: string;
      fingerprint: string;
      termsVersion: string;
      deviceInfo: {
        userAgent: string;
        language: string;
        platform: string;
        screenResolution: string;
        colorDepth: number;
        timezone: string;
      }
    }) => api.post(`/requests/${requestUid}/contracts/signature`, data),

    uploadDocuments: (requestUid: string, data: FormData) => api.post(`/requests/${requestUid}/documents`, data),

    startIdentityVerification: (requestUid: string) => api.get(`/requests/${requestUid}/identity-verification`),

    getStatus: (requestUid: string) => api.get(`/requests/${requestUid}/status`),
    
    updateCbu: (requestUid: string, data: {
      cbu: string;
    }) => api.post(`/requests/${requestUid}/bank`, data),

    // Lead Generation
    getFromLeadGen: (uid: string, dniLastDigits: string) => api.post(`/requests/${uid}/from-leadgen`, {
      dniLastDigits
    }),
  },
  
};

export default api; 